/*
 * newsbot - Filter (Category) details/edit page with tabbed layout
 * Fork of autobrr Details.tsx pattern
 */

import { useRef, useState } from "react";
import { useSuspenseQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams, Link } from "@tanstack/react-router";
import { Formik, Form } from "formik";

import { useToggle } from "@hooks/hooks";
import { classNames } from "@utils";
import { toast } from "@components/hot-toast";
import Toast from "@components/notifications/Toast";
import { FormErrorNotification } from "@components/notifications/FormErrorNotification";
import { DeleteModal } from "@components/modals";
import { categoryKeys, useFilterActions } from "@api/queries";
import { api } from "@api/client";
import type { CategoryWithStats, UpdateCategoryInput } from "@/types";
import { General } from "./sections/General";
import { Content } from "./sections/Content";
import { Actions } from "./sections/Actions";

// Tab types
type TabName = "general" | "content" | "actions";

interface Tab {
  id: TabName;
  name: string;
}

const tabs: Tab[] = [
  { id: "general", name: "General" },
  { id: "content", name: "Content" },
  { id: "actions", name: "Actions" },
];

interface TabNavLinkProps {
  tab: Tab;
  isActive: boolean;
  onClick: () => void;
}

function TabNavLink({ tab, isActive, onClick }: TabNavLinkProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={classNames(
        "whitespace-nowrap pb-3 pt-1 px-1",
        isActive
          ? "border-b-2 border-blue-600 dark:border-blue-500"
          : "border-b-2 border-transparent"
      )}
    >
      <span
        className={classNames(
          "font-medium text-sm",
          isActive
            ? "text-blue-600 dark:text-white"
            : "text-gray-500 dark:text-gray-400 hover:text-blue-600 dark:hover:text-white"
        )}
      >
        {tab.name}
      </span>
    </button>
  );
}

interface FilterFormValues {
  id: string;
  name: string;
  slug: string;
  keywords: string;
  except_keywords: string;
  match_logic: "ANY" | "ALL";
  except_logic: "ANY" | "ALL";
  priority: number;
  enabled: boolean;
  // Source filtering
  match_sources: string;
  except_sources: string;
  sources_match_logic: "ANY" | "ALL";
  sources_except_logic: "ANY" | "ALL";
  // Entity filtering
  match_entities: string;
  except_entities: string;
  entities_match_logic: "ANY" | "ALL";
  entities_except_logic: "ANY" | "ALL";
  // Author filtering
  match_authors: string;
  except_authors: string;
  authors_match_logic: "ANY" | "ALL";
  authors_except_logic: "ANY" | "ALL";
  // Importance threshold
  min_importance: number | null;
  actions: unknown[];
}

const validateFilter = (values: FilterFormValues) => {
  const errors: Partial<Record<keyof FilterFormValues, string>> = {};

  if (!values.name || values.name.trim().length === 0) {
    errors.name = "Name is required";
  } else if (values.name.length > 100) {
    errors.name = "Name must be 100 characters or less";
  }

  if (!values.slug || values.slug.trim().length === 0) {
    errors.slug = "Slug is required";
  } else if (values.slug.length > 100) {
    errors.slug = "Slug must be 100 characters or less";
  } else if (!/^[a-z0-9-]+$/.test(values.slug)) {
    errors.slug = "Slug must contain only lowercase letters, numbers, and hyphens";
  }

  if (values.priority < -1000 || values.priority > 1000) {
    errors.priority = "Priority must be between -1000 and 1000";
  }

  return errors;
};

interface FormButtonsGroupProps {
  isSubmitting: boolean;
  isValid: boolean;
  isSystemCategory: boolean;
  onDelete: () => void;
  onCancel: () => void;
  onReset: () => void;
}

function FormButtonsGroup({
  isSubmitting,
  isValid,
  isSystemCategory,
  onDelete,
  onCancel,
  onReset,
}: FormButtonsGroupProps) {
  return (
    <div className="px-0.5 mt-8 flex flex-col-reverse sm:flex-row flex-wrap-reverse justify-between border-t border-gray-200 dark:border-gray-700 pt-6">
      <div>
        {!isSystemCategory && (
          <button
            type="button"
            onClick={onDelete}
            className="flex items-center justify-center px-4 py-2 rounded-md sm:text-sm transition bg-red-700 dark:bg-red-900 dark:hover:bg-red-700 hover:bg-red-800 text-white focus:outline-hidden"
          >
            Delete Filter
          </button>
        )}
      </div>

      <div className="flex justify-between mb-4 sm:mb-0 gap-2">
        <button
          type="button"
          onClick={onReset}
          className="bg-white dark:bg-gray-700 py-2 px-4 border border-gray-300 dark:border-gray-600 transition rounded-md shadow-xs text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          Reset
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="bg-white dark:bg-gray-700 py-2 px-4 border border-gray-300 dark:border-gray-600 transition rounded-md shadow-xs text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-hidden"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={isSubmitting || !isValid}
          className="flex items-center px-4 py-2 border border-transparent transition shadow-xs text-sm font-medium rounded-md text-white bg-blue-600 dark:bg-blue-600 hover:bg-blue-700 dark:hover:bg-blue-700 focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSubmitting ? "Saving..." : "Save"}
        </button>
      </div>
    </div>
  );
}

export function FilterDetails() {
  const { filterId } = useParams({ strict: false });
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const cancelModalButtonRef = useRef(null);
  const [deleteModalOpen, toggleDeleteModal] = useToggle(false);
  const [activeTab, setActiveTab] = useState<TabName>("general");

  if (!filterId) {
    throw new Error("Filter ID is required");
  }

  const { data: category } = useSuspenseQuery({
    queryKey: categoryKeys.detail(filterId),
    queryFn: () => api.get<CategoryWithStats>(`/categories/${filterId}`),
  });

  const { data: actions = [] } = useFilterActions(filterId);

  const updateMutation = useMutation({
    mutationFn: (data: UpdateCategoryInput) =>
      api.put<CategoryWithStats>(`/categories/${filterId}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: categoryKeys.all });
      queryClient.invalidateQueries({ queryKey: categoryKeys.withStats });
      queryClient.invalidateQueries({ queryKey: categoryKeys.detail(filterId) });
      queryClient.invalidateQueries({ queryKey: categoryKeys.stats });
      toast.custom((t) => (
        <Toast type="success" body={`Filter "${category.name}" updated successfully`} t={t} />
      ));
      navigate({ to: "/filters" });
    },
    onError: (error) => {
      toast.custom((t) => (
        <Toast type="error" body={`Failed to update filter: ${String(error)}`} t={t} />
      ));
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => api.delete(`/categories/${filterId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: categoryKeys.all });
      queryClient.invalidateQueries({ queryKey: categoryKeys.withStats });
      queryClient.invalidateQueries({ queryKey: categoryKeys.stats });
      toast.custom((t) => (
        <Toast type="success" body={`Filter "${category.name}" deleted`} t={t} />
      ));
      navigate({ to: "/filters" });
    },
    onError: (error) => {
      toast.custom((t) => (
        <Toast type="error" body={`Failed to delete filter: ${String(error)}`} t={t} />
      ));
    },
  });

  const initialValues: FilterFormValues = {
    id: filterId,
    name: category.name,
    slug: category.slug,
    keywords: category.keywords.join(", "),
    except_keywords: category.except_keywords?.join(", ") ?? "",
    match_logic: category.match_logic ?? "ANY",
    except_logic: category.except_logic ?? "ANY",
    priority: category.priority,
    enabled: category.enabled,
    // Source filtering
    match_sources: category.match_sources?.join(", ") ?? "",
    except_sources: category.except_sources?.join(", ") ?? "",
    sources_match_logic: category.sources_match_logic ?? "ANY",
    sources_except_logic: category.sources_except_logic ?? "ANY",
    // Entity filtering
    match_entities: category.match_entities?.join(", ") ?? "",
    except_entities: category.except_entities?.join(", ") ?? "",
    entities_match_logic: category.entities_match_logic ?? "ANY",
    entities_except_logic: category.entities_except_logic ?? "ANY",
    // Author filtering
    match_authors: category.match_authors?.join(", ") ?? "",
    except_authors: category.except_authors?.join(", ") ?? "",
    authors_match_logic: category.authors_match_logic ?? "ANY",
    authors_except_logic: category.authors_except_logic ?? "ANY",
    // Importance threshold
    min_importance: category.min_importance,
    actions: actions,
  };

  // Helper to parse comma-separated string into array
  const parseCommaSeparated = (value: string): string[] =>
    value.split(",").map((k) => k.trim()).filter((k) => k.length > 0);

  const handleSubmit = (values: FilterFormValues) => {
    updateMutation.mutate({
      name: values.name,
      slug: values.slug,
      keywords: parseCommaSeparated(values.keywords),
      except_keywords: parseCommaSeparated(values.except_keywords),
      match_logic: values.match_logic,
      except_logic: values.except_logic,
      priority: values.priority,
      enabled: values.enabled,
      // Source filtering
      match_sources: parseCommaSeparated(values.match_sources),
      except_sources: parseCommaSeparated(values.except_sources),
      sources_match_logic: values.sources_match_logic,
      sources_except_logic: values.sources_except_logic,
      // Entity filtering
      match_entities: parseCommaSeparated(values.match_entities),
      except_entities: parseCommaSeparated(values.except_entities),
      entities_match_logic: values.entities_match_logic,
      entities_except_logic: values.entities_except_logic,
      // Author filtering
      match_authors: parseCommaSeparated(values.match_authors),
      except_authors: parseCommaSeparated(values.except_authors),
      authors_match_logic: values.authors_match_logic,
      authors_except_logic: values.authors_except_logic,
      // Importance threshold
      min_importance: values.min_importance,
    });
  };

  const handleDelete = () => {
    deleteMutation.mutate();
    toggleDeleteModal();
  };

  const isSystemCategory = category.user_id === null;

  return (
    <main>
      {/* Header with breadcrumb */}
      <div className="my-6 max-w-(--breakpoint-xl) mx-auto px-4 sm:px-6 lg:px-8">
        <nav aria-label="Breadcrumb" className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
          <Link to="/filters" className="hover:text-gray-700 dark:hover:text-gray-200">
            Filters
          </Link>
          <span aria-hidden="true">/</span>
        </nav>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white mt-1 truncate" title={category.name}>
          {category.name}
        </h1>
      </div>

      {/* System category warning */}
      {isSystemCategory && (
        <div className="max-w-(--breakpoint-xl) mx-auto px-4 sm:px-6 lg:px-8 mb-4">
          <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-md">
            <p className="text-sm text-yellow-800 dark:text-yellow-200">
              This is a system default filter. Your changes will override the default for your account.
            </p>
          </div>
        </div>
      )}

      {/* Main content */}
      <div className="max-w-(--breakpoint-xl) mx-auto pb-12 px-2 sm:px-6 lg:px-8">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-250 dark:border-gray-775">
          {/* Tab navigation */}
          <div className="rounded-t-lg bg-gray-125 dark:bg-gray-850 border-b border-gray-200 dark:border-gray-750">
            <nav className="px-4 py-2 -mb-px flex space-x-6 sm:space-x-8 overflow-x-auto">
              {tabs.map((tab) => (
                <TabNavLink
                  key={tab.id}
                  tab={tab}
                  isActive={activeTab === tab.id}
                  onClick={() => setActiveTab(tab.id)}
                />
              ))}
            </nav>
          </div>

          {/* Form */}
          <Formik
            initialValues={initialValues}
            validate={validateFilter}
            onSubmit={handleSubmit}
            enableReinitialize
          >
            {({ isSubmitting, isValid, resetForm }) => (
              <Form className="pt-2 pb-4 px-5">
                {/* Form-level error notification */}
                <FormErrorNotification />

                {/* Tab content */}
                <div className="py-2">
                  {activeTab === "general" && <General />}
                  {activeTab === "content" && <Content />}
                  {activeTab === "actions" && <Actions />}
                </div>

                {/* Form buttons */}
                <FormButtonsGroup
                  isSubmitting={isSubmitting}
                  isValid={isValid}
                  isSystemCategory={isSystemCategory}
                  onDelete={toggleDeleteModal}
                  onCancel={() => navigate({ to: "/filters" })}
                  onReset={() => {
                    resetForm();
                    toast.custom((t) => (
                      <Toast type="success" body="Reset all filter values." t={t} />
                    ));
                  }}
                />
              </Form>
            )}
          </Formik>
        </div>

        {/* Delete confirmation modal */}
        <DeleteModal
          isOpen={deleteModalOpen}
          isLoading={deleteMutation.isPending}
          toggle={toggleDeleteModal}
          buttonRef={cancelModalButtonRef}
          deleteAction={handleDelete}
          title={`Remove filter: ${category.name}`}
          text="Are you sure you want to remove this filter? This action cannot be undone."
        />
      </div>
    </main>
  );
}
